"use client";

import React from "react";
import { VisitStatusProps, VisitStatus as VisitStatusEnum } from "./types";
import { defaultVisitStatusTheme } from "./themes";
import { mergeTheme } from "../utils/theme";

export const VisitStatus: React.FC<VisitStatusProps> = ({
  status,
  hasUnreadMessages = false,
  timestamp,
  theme,
  className = "",
}) => {
  const mergedTheme = mergeTheme(defaultVisitStatusTheme, theme);

  // Format timestamp if provided
  const formatTimestamp = (ts: string | Date): string => {
    const date = typeof ts === "string" ? new Date(ts) : ts;
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Get status-specific classes
  const getStatusClasses = () => {
    const textClasses = [
      mergedTheme.text?.base,
      mergedTheme.text?.[status as keyof typeof mergedTheme.text],
    ]
      .filter(Boolean)
      .join(" ");

    const iconClasses = [
      mergedTheme.icon?.base,
      mergedTheme.icon?.[status as keyof typeof mergedTheme.icon],
    ]
      .filter(Boolean)
      .join(" ");

    return {
      text: textClasses,
      icon: iconClasses,
    };
  };

  const classes = getStatusClasses();

  // Render status icon based on status type
  const renderIcon = () => {
    if (status === VisitStatusEnum.ACTIVE && hasUnreadMessages) {
      return (
        <svg
          className={`status-icon message ${classes.icon}`}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 0C3.58 0 0 3.58 0 8c0 1.42.37 2.75 1.02 3.9L0 16l4.1-1.02C5.25 15.63 6.58 16 8 16c4.42 0 8-3.58 8-8s-3.58-8-8-8z" />
        </svg>
      );
    }

    switch (status) {
      case VisitStatusEnum.REQUESTED:
        return (
          <svg
            className={`status-icon clock ${classes.icon}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <circle
              cx="8"
              cy="8"
              r="7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            />
            <path
              d="M8 4v4l3 3"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        );
      case VisitStatusEnum.ACTIVE:
        return (
          <svg
            className={`status-icon active ${classes.icon}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <circle cx="8" cy="8" r="6" />
          </svg>
        );
      case VisitStatusEnum.COMPLETED:
        return (
          <svg
            className={`status-icon check ${classes.icon}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M6 10.17L2.83 7 1.41 8.41 6 13 15 4 13.59 2.59z" />
          </svg>
        );
      case VisitStatusEnum.CANCELLED:
        return (
          <svg
            className={`status-icon cancel ${classes.icon}`}
            viewBox="0 0 16 16"
            fill="currentColor"
          >
            <path d="M8 0C3.58 0 0 3.58 0 8s3.58 8 8 8 8-3.58 8-8-3.58-8-8-8zm3.59 10.41L10.17 11.59 8 9.41 5.83 11.59 4.41 10.17 6.59 8 4.41 5.83 5.83 4.41 8 6.59 10.17 4.41 11.59 5.83 9.41 8z" />
          </svg>
        );
    }
    return null;
  };

  // Get display text for status
  const getStatusText = () => {
    switch (status) {
      case VisitStatusEnum.REQUESTED:
        return "Requested";
      case VisitStatusEnum.ACTIVE:
        return hasUnreadMessages ? "New Message" : "Active";
      case VisitStatusEnum.COMPLETED:
        return "Completed";
      case VisitStatusEnum.CANCELLED:
        return "Cancelled";
    }
    // This should never be reached with our typed enum, but providing fallback
    return (
      (status as string).charAt(0).toUpperCase() + (status as string).slice(1)
    );
  };

  return (
    <div className={`visit-status ${mergedTheme.container} ${className}`}>
      {renderIcon()}
      <span className={`status-text ${classes.text}`}>{getStatusText()}</span>
      {timestamp && (
        <span className={`status-timestamp ${mergedTheme.timestamp}`}>
          {formatTimestamp(timestamp)}
        </span>
      )}
    </div>
  );
};
